import type { CompanyDetails } from '../../shared/services/companyDetailsService/types';

/**
 * Checks if an array or string has meaningful content
 */
const hasValidContent = (
  content: (string | null) | (string | null)[],
): boolean => {
  if (Array.isArray(content)) {
    return (
      content.length > 0 &&
      content.some((item) => Boolean(item && String(item).trim()))
    );
  }
  return Boolean(content);
};

/**
 * Checks if gallery content exists and has photos
 */
const hasGalleryContent = (
  gallery: CompanyDetails['companyProfile']['data']['gallery'],
): boolean => {
  return Array.isArray(gallery) && gallery.length > 0;
};

/**
 * Checks if awards content exists
 */
const hasAwardsContent = (
  awards: CompanyDetails['companyProfile']['data']['awards'],
): boolean => {
  return Array.isArray(awards) && awards.length > 0;
};

/**
 * Checks if featured content has any meaningful data
 */
const hasFeaturedContent = (
  featured: CompanyDetails['companyProfile']['data']['featured'],
): boolean => {
  if (!featured) return false;

  return Boolean(
    featured.title ||
      featured.description ||
      featured.imageUrl ||
      featured.videoUrl,
  );
};

/**
 * Checks if overview section has substantial content
 */
const hasOverviewContent = (
  overview: CompanyDetails['companyProfile']['data']['overview'],
): boolean => {
  if (!overview) return false;

  return Boolean(
    overview.industry ||
      overview.primaryLocation ||
      overview.website ||
      hasValidContent(overview.description) ||
      hasValidContent(overview.specialities) ||
      overview.size ||
      overview.traditionalLand ||
      overview.traditionalPlace,
  );
};

/**
 * Checks if perks and benefits content exists and has meaningful data
 */
const hasPerksAndBenefitsContent = (
  perksAndBenefits: CompanyDetails['companyProfile']['data']['perksAndBenefits'],
): boolean => {
  return (
    Array.isArray(perksAndBenefits) &&
    perksAndBenefits.length > 0 &&
    perksAndBenefits.some((perk) =>
      Boolean(
        perk && typeof perk === 'object' && 'title' in perk && perk.title,
      ),
    )
  );
};

/**
 * Checks if culture and values content exists and has meaningful data
 */
const hasCultureAndValuesContent = (
  cultureAndValues: CompanyDetails['companyProfile']['data']['cultureAndValues'],
): boolean => {
  if (!cultureAndValues) return false;

  return Boolean(
    cultureAndValues.title ||
      hasValidContent(cultureAndValues.description) ||
      cultureAndValues.imageUrl ||
      (Array.isArray(cultureAndValues.values) &&
        cultureAndValues.values.length > 0 &&
        cultureAndValues.values.some((value) => Boolean(value?.title))),
  );
};

/**
 * Checks if custom statement content exists and has meaningful data
 */
const hasCustomStatementContent = (
  customStatement: CompanyDetails['companyProfile']['data']['customStatement'],
): boolean => {
  if (!customStatement) return false;

  return Boolean(
    customStatement.title || hasValidContent(customStatement.description),
  );
};

/**
 * Checks if a company is a test account.
 * This is a temporary solution to prevent the test accounts to be indexed, we will need to implement this according to test record in the future
 *
 * @param companyId - The ID of the company
 * @returns true if the company is a test account, false otherwise
 */
export const isTestAccount = (companyDetails: CompanyDetails): boolean =>
  ['172965814081565', '170537282833559'].includes(companyDetails.id as string);

/**
 * Determines whether a company profile should be indexed by search engines.
 * A page is considered indexable if it has substantial content in any of the key areas.
 *
 * @param companyDetails - The company details
 * @returns true if the page should be indexed, false if indexing should be disabled
 */
export const calculateAboutPageShouldIndex = ({
  companyDetails,
}: {
  companyDetails: CompanyDetails;
}): boolean => {
  const companyProfileData = companyDetails.companyProfile?.data;
  const companyReviewsAISummary = companyDetails.companyReviewsAISummary;

  // Check each content area for substantial content
  const contentChecks = [
    Boolean(companyReviewsAISummary),
    hasGalleryContent(companyProfileData?.gallery),
    hasValidContent(companyProfileData?.missionStatement),
    hasAwardsContent(companyProfileData?.awards),
    hasFeaturedContent(companyProfileData?.featured),
    hasOverviewContent(companyProfileData?.overview),
  ];

  // Return true if any content area has substantial content
  return contentChecks.some(Boolean);
};

/**
 * Determines whether a company culture page should be indexed by search engines.
 * A page is considered indexable if it has substantial content in any of the key areas:
 * perks and benefits, culture and values, custom statement, or gallery.
 *
 * @param companyProfile - The company profile
 * @returns true if the page should be indexed, false if indexing should be disabled
 */
export const calculateCulturePageShouldIndex = ({
  companyProfile,
}: {
  companyProfile: CompanyDetails['companyProfile'];
}): boolean => {
  const companyProfileData = companyProfile?.data;

  // Check each content area for substantial content
  const contentChecks = [
    hasPerksAndBenefitsContent(companyProfileData?.perksAndBenefits),
    hasCultureAndValuesContent(companyProfileData?.cultureAndValues),
    hasCustomStatementContent(companyProfileData?.customStatement),
    hasGalleryContent(companyProfileData?.gallery),
  ];

  // Return true if any content area has substantial content
  return contentChecks.some(Boolean);
};
